import { useState, useEffect, useCallback } from 'react';

interface NetworkRequest {
  id: string;
  method: string;
  url: string;
  status: number;
  duration: number;
  timestamp: number;
  success: boolean;
  size?: number;
  type?: string;
}

interface UseNetworkSimulationReturn {
  requests: NetworkRequest[];
  isRecording: boolean;
  toggleRecording: () => void;
  clearHistory: () => void;
  exportData: () => void;
}

export function useNetworkSimulation(): UseNetworkSimulationReturn {
  const [requests, setRequests] = useState<NetworkRequest[]>([]);
  const [isRecording, setIsRecording] = useState(true);
  const [requestId, setRequestId] = useState(0);

  // 네트워크 요청 시뮬레이션
  useEffect(() => {
    if (!isRecording) return;

    const simulateRequest = () => {
      const methods = ['GET', 'POST', 'PUT', 'DELETE'];
      const urls = [
        'https://api.example.com/users',
        'https://api.example.com/posts',
        'https://api.example.com/auth/login',
        'https://api.example.com/dashboard/stats',
        'https://api.example.com/settings',
        'https://api.example.com/files/upload',
        'https://api.example.com/notifications',
        'https://jsonplaceholder.typicode.com/posts',
        'https://jsonplaceholder.typicode.com/users',
        'https://httpbin.org/get',
      ];

      const method = methods[Math.floor(Math.random() * methods.length)];
      const url = urls[Math.floor(Math.random() * urls.length)];
      const duration = 50 + Math.random() * 2000; // 50ms ~ 2050ms
      const isError = Math.random() < 0.15; // 15% 에러율
      const status = isError 
        ? (Math.random() > 0.5 ? 404 : 500) 
        : (Math.random() > 0.8 ? 201 : 200);
      
      const newRequest: NetworkRequest = {
        id: `req-${requestId}`,
        method,
        url,
        status,
        duration: Math.round(duration),
        timestamp: Date.now(),
        success: !isError,
        size: Math.round(1024 + Math.random() * 10240), // 1KB ~ 11KB
        type: method === 'GET' ? 'xhr' : 'fetch',
      };

      setRequests(prev => [...prev, newRequest].slice(-100)); // 최대 100개 유지
      setRequestId(prev => prev + 1);
    };

    // 초기 요청 몇 개 생성
    if (requests.length === 0) {
      for (let i = 0; i < 5; i++) {
        setTimeout(() => simulateRequest(), i * 500);
      }
    }

    // 주기적으로 새 요청 생성
    const interval = setInterval(() => {
      if (Math.random() > 0.3) { // 70% 확률로 요청 생성
        simulateRequest();
      }
    }, 3000 + Math.random() * 5000); // 3~8초 간격

    return () => clearInterval(interval);
  }, [isRecording, requestId, requests.length]);

  const toggleRecording = useCallback(() => {
    setIsRecording(prev => !prev);
  }, []);

  const clearHistory = useCallback(() => {
    setRequests([]);
    setRequestId(0);
  }, []);

  const exportData = useCallback(() => {
    const dataStr = JSON.stringify(requests, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `network-requests-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  }, [requests]);

  return {
    requests,
    isRecording,
    toggleRecording,
    clearHistory,
    exportData,
  };
}
